import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, ChevronLeft, Clock, AlertCircle, CheckCircle2, XCircle, Send, ThumbsUp, ThumbsDown } from "lucide-react";

const STATUS_META = {
  open:             { label: "Open · Board reviewing",  tone: "bg-secondary/15 text-secondary" },
  awaiting_owner:   { label: "Awaiting your response",  tone: "bg-[hsl(32_87%_62%/0.18)] text-[hsl(32_60%_35%)]" },
  approved:         { label: "Approved",                tone: "bg-[hsl(140_55%_45%/0.18)] text-[hsl(140_65%_45%)]" },
  denied:           { label: "Denied",                  tone: "bg-destructive/15 text-destructive" },
};

export default function Inquiries() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionForm, setDecisionForm] = useState({ approved: true, notes: "", next_steps: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/inquiries");
      setItems(data);
      if (selected) {
        const fresh = data.find((x) => x.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (err) {
      console.error("inquiries load failed:", err);
      toast.error(err.response?.data?.detail || "Could not load");
    }
  }, [selected]);

  useEffect(() => { load(); }, [load]);

  const sendReply = async (askMoreInfo = false) => {
    if (!reply.trim()) return toast.error("Type a reply first");
    setBusy(true);
    try {
      await api.post(`/inquiries/${selected.id}/messages`, {
        body: reply,
        request_more_info: askMoreInfo,
      });
      setReply("");
      const { data } = await api.get(`/inquiries/${selected.id}`);
      setSelected(data);
      await load();
      toast.success(askMoreInfo ? "Sent — awaiting owner response" : "Reply sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  const decide = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/inquiries/${selected.id}/decision`, decisionForm);
      setDecisionOpen(false);
      toast.success(decisionForm.approved ? "Inquiry approved" : "Inquiry denied");
      const { data } = await api.get(`/inquiries/${selected.id}`);
      setSelected(data);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save decision");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div>
        <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
          <MessageSquare className="w-4 h-4" /> Owner ↔ Board communications
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">{isBoard ? "Owner requests" : "My inquiries"}</h1>
        <p className="text-foreground/70 mt-1">{isBoard ? "Every owner request in one queue — with SLA clocks and audit-safe decisions." : "Track your inquiries to the board and their responses."}</p>
      </div>

      {selected ? (
        <div className="hoa-card royal-ring p-6" data-testid="inquiry-detail">
          <button className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" onClick={() => setSelected(null)} data-testid="inquiry-back-btn">
            <ChevronLeft className="w-4 h-4" /> Back to queue
          </button>
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="text-2xl font-semibold">{selected.subject}</h2>
              <div className="text-sm text-muted-foreground mt-1">
                {selected.owner_name} · Unit {selected.owner_unit || "—"} · {selected.category} · {new Date(selected.created_at).toLocaleString()}
              </div>
            </div>
            <div className="text-right">
              <SlaChip sla={selected.sla} status={selected.status} />
              <div className="mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_META[selected.status]?.tone || ""}`} data-testid="inquiry-status-chip">
                  {STATUS_META[selected.status]?.label || selected.status}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {(selected.messages || []).map((m) => (
              <div key={m.id} className={`p-3 rounded-lg border ${m.from_role === "owner" ? "bg-accent/30 border-border" : "bg-primary/10 border-primary/30"}`} data-testid={`inquiry-msg-${m.id}`}>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span className="font-medium text-gold">{m.from_name} · {m.from_role}</span>
                  <span>{new Date(m.at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-foreground/85 whitespace-pre-wrap">{m.body}</p>
                {m.request_more_info && <div className="text-xs text-secondary mt-1 italic">↳ requested more info from owner</div>}
              </div>
            ))}
          </div>

          {selected.decision ? (
            <div className={`p-4 rounded-lg border ${selected.decision.approved ? "bg-[hsl(140_55%_45%/0.12)] border-[hsl(140_55%_45%/0.35)]" : "bg-destructive/10 border-destructive/30"}`} data-testid="inquiry-decision">
              <div className="flex items-center gap-2 mb-1">
                {selected.decision.approved ? <CheckCircle2 className="w-5 h-5 text-[hsl(140_65%_45%)]" /> : <XCircle className="w-5 h-5 text-destructive" />}
                <span className="font-semibold">{selected.decision.approved ? "Approved" : "Denied"}</span>
                <span className="text-xs text-muted-foreground">by {selected.decision.by} · {new Date(selected.decision.at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-foreground/85">{selected.decision.notes}</p>
              {selected.decision.next_steps && (
                <div className="mt-2 pt-2 border-t border-border/50 text-sm">
                  <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-1">Next steps</div>
                  <p className="text-foreground/85">{selected.decision.next_steps}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder={isBoard ? "Reply to the owner…" : "Add more info or ask a follow-up…"} className="min-h-[90px]" data-testid="inquiry-reply-input" />
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" className="rounded-full" onClick={() => sendReply(false)} disabled={busy} data-testid="inquiry-reply-btn">
                  <Send className="w-4 h-4 mr-1.5" /> Send reply
                </Button>
                {isBoard && (
                  <>
                    <Button variant="outline" className="rounded-full" onClick={() => sendReply(true)} disabled={busy} data-testid="inquiry-request-info-btn">
                      <AlertCircle className="w-4 h-4 mr-1.5" /> Request more info
                    </Button>
                    <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
                      <DialogTrigger asChild>
                        <Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="inquiry-decide-btn">
                          <ThumbsUp className="w-4 h-4 mr-1.5" /> Decide
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Approve or deny the inquiry</DialogTitle></DialogHeader>
                        <form onSubmit={decide} className="space-y-3" data-testid="decision-form">
                          <div className="flex gap-2">
                            <Button type="button" variant={decisionForm.approved ? "default" : "outline"} className="rounded-full flex-1" onClick={() => setDecisionForm({ ...decisionForm, approved: true })} data-testid="dec-approve-btn">
                              <ThumbsUp className="w-4 h-4 mr-1.5" /> Approve
                            </Button>
                            <Button type="button" variant={!decisionForm.approved ? "default" : "outline"} className="rounded-full flex-1" onClick={() => setDecisionForm({ ...decisionForm, approved: false })} data-testid="dec-deny-btn">
                              <ThumbsDown className="w-4 h-4 mr-1.5" /> Deny
                            </Button>
                          </div>
                          <div>
                            <Label>Decision notes</Label>
                            <Textarea required value={decisionForm.notes} onChange={(e) => setDecisionForm({ ...decisionForm, notes: e.target.value })} className="mt-1.5 min-h-[80px]" data-testid="dec-notes-input" />
                          </div>
                          <div>
                            <Label>Next steps (optional)</Label>
                            <Input value={decisionForm.next_steps} onChange={(e) => setDecisionForm({ ...decisionForm, next_steps: e.target.value })} className="mt-1.5 h-11" placeholder="e.g. Payment plan · CoI required · Attend Nov meeting" data-testid="dec-next-steps-input" />
                          </div>
                          <DialogFooter>
                            <Button type="submit" disabled={busy} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="dec-submit-btn">
                              {busy ? "Saving…" : "Record decision"}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="hoa-card overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <div className="col-span-4">Subject</div>
            <div className="col-span-2">{isBoard ? "Owner" : "Category"}</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">SLA</div>
            <div className="col-span-2 text-right">Created</div>
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="inquiries-empty">
              No inquiries yet. {isBoard ? "When owners submit questions from their balance page, they land here." : "Head to your balance page and click 'Ask the Board'."}
            </div>
          ) : items.map((i) => (
            <button
              type="button"
              key={i.id}
              onClick={() => setSelected(i)}
              className="grid grid-cols-12 gap-3 px-4 py-4 border-b border-border/50 items-center w-full text-left hover:bg-accent/30 transition-colors"
              data-testid={`inquiry-row-${i.id}`}
            >
              <div className="col-span-4">
                <div className="font-semibold">{i.subject}</div>
                <div className="text-xs text-muted-foreground line-clamp-1">{i.body}</div>
              </div>
              <div className="col-span-2 text-xs">{isBoard ? `${i.owner_name} · ${i.owner_unit || "—"}` : i.category}</div>
              <div className="col-span-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_META[i.status]?.tone || ""}`}>{STATUS_META[i.status]?.label || i.status}</span>
              </div>
              <div className="col-span-2"><SlaChip sla={i.sla} status={i.status} /></div>
              <div className="col-span-2 text-right text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SlaChip({ sla, status }) {
  if (!sla) return null;
  if (sla.closed) return <span className="text-xs text-muted-foreground">Closed</span>;
  const tone = sla.overdue ? "bg-destructive/15 text-destructive" : sla.hours_remaining < 24 ? "bg-[hsl(32_87%_62%/0.18)] text-[hsl(32_60%_35%)]" : "bg-primary/15 text-gold";
  const h = Math.abs(sla.hours_remaining);
  const label = sla.overdue ? `Overdue ${Math.round(h)}h` : h < 48 ? `${Math.round(h)}h left` : `${Math.round(h / 24)}d left`;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${tone}`} data-testid="sla-chip">
      <Clock className="w-3 h-3" /> {label}
    </span>
  );
}
